import { Check, ClipboardCheck, Pencil, RotateCcw, Star } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { usePermission } from '../../../../auth/AuthContext';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import type { DataColumn } from '../../../../components/data-table/DataTable';
import { DataTablePanel } from '../../../../components/data-table/DataTablePanel';
import { useDataTable, type DataTableQuery } from '../../../../components/data-table/useDataTable';
import { progressPercent } from '../../../../components/progressPercent';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { usePersonNames } from '../../personnel/usePersonNames';
import { nomineeName } from '../nomineeName';
import { evaluationsApi, nominationsApi } from '../praiseApi';
import type { PraiseSurface } from '../praiseSurface';
import type { AwardDetail, AwardNomineeDetail, AwardNomineeListItem, AwardStageGate } from '../praiseTypes';
import { NomineeModal } from './NomineeModal';
import { QualifyNomineeModal } from './QualifyNomineeModal';
import { ScoreNomineeModal } from './ScoreNomineeModal';

/**
 * One award's nominees — the Nominees tab of the award's profile, on BOTH surfaces. Nomination
 * registers, edits, deletes and restores them; Evaluation reads them, and each row opens the
 * nominee's own profile with every document they submitted.
 *
 * Server-side search, sort and paging. **The NAME can be neither searched nor sorted**: it is in the
 * other database, and it resolves ONE batch per page through PRAISE's own scoped door — reading
 * nominations never means reading the 201 files.
 */
export function NomineesPanel({
  surface,
  award,
  onChanged,
  stage,
  onStageCounts,
  fill = true,
}: {
  surface: PraiseSurface;
  award: AwardDetail;
  /** The award's nominee count moved — the profile refetches its badge. */
  onChanged: () => void;
  /**
   * The STAGE GATE the table is read at (Evaluation's rail, 03 Nov 2026) — it narrows the rows, picks
   * the stage's columns and its row action. Absent on Nomination, which lists every nominee.
   */
  stage?: AwardStageGate;
  /** Each stage's count, off the server's `stage` facet — what the rail shows. */
  onStageCounts?: (counts: Partial<Record<AwardStageGate, number>>) => void;
  /**
   * Pins the pager to the panel's bottom edge (the default). FALSE where a card sits ABOVE the table —
   * the table is then sized by its rows and the column around it is the one scroll region.
   */
  fill?: boolean;
}) {
  const navigate = useNavigate();
  const toast = useToast();
  const writes = surface.writes;
  const canReadPersonnel = usePermission('personnel.read');
  // On Evaluation the reader holds its key by reaching the page; on Nomination it is a second grant.
  const holdsEvaluationRead = usePermission('award-evaluations.read');
  const canReadEvaluation = surface.key === 'evaluation' || holdsEvaluationRead;

  const [showDeleted, setShowDeleted] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<AwardNomineeListItem | null>(null);
  const [editingDetail, setEditingDetail] = useState<AwardNomineeDetail | null>(null);
  /** One nominee from the row menu, or several from the selection bar — one dialog either way. */
  const [deleting, setDeleting] = useState<AwardNomineeListItem[] | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  /** The stage acts — one nominee being tagged, or scored (the detail the score sheet needs). */
  const [qualifying, setQualifying] = useState<AwardNomineeListItem | null>(null);
  const [scoring, setScoring] = useState<{ nominee: AwardNomineeDetail; name: string | null } | null>(null);

  const api = surface.api;

  const fetchPage = useCallback(
    async (query: DataTableQuery, signal: AbortSignal) => {
      const page = await api.listNominees(
        award.id,
        { search: query.search, page: query.page, pageSize: query.pageSize, sort: query.sort, includeDeleted: showDeleted, stage },
        signal,
      );

      onStageCounts?.(Object.fromEntries((page.facets?.stage ?? []).map((f) => [f.value, f.count])));

      return { items: page.items, total: page.total };
    },
    [api, award.id, showDeleted, stage, onStageCounts],
  );

  // Unsorted means the server's order: most recently nominated first.
  const state = useDataTable<AwardNomineeListItem>({ fetchPage, pageSize: 10 });

  // Only the ids that came back; `!= null` because an absent one is omitted from the wire.
  const personIds = useMemo(() => state.rows.map((n) => n.personId).filter((id): id is string => id != null), [state.rows]);
  const names = usePersonNames(personIds, api.names);

  const refresh = useCallback(() => {
    state.reload();
    onChanged();
  }, [state, onChanged]);

  async function confirmDelete() {
    if (!deleting) return;

    setRunning(true);
    setError(null);

    let done = 0;

    try {
      // One request per nominee, so each lands in its own record's History Logs.
      for (const nominee of deleting) {
        await nominationsApi.removeNominee(nominee.id);
        done += 1;
      }
      toast.success(deleting.length === 1 ? 'The nomination was deleted.' : `${deleting.length} nominations were deleted.`);
      setDeleting(null);
      refresh();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('Could not delete the nomination.', 0));
      // A run stopped partway still deleted some; show what is now true.
      if (done > 0) refresh();
    } finally {
      setRunning(false);
    }
  }

  async function restore(nominee: AwardNomineeListItem) {
    try {
      // The undo takes no ceremony.
      await nominationsApi.restoreNominee(nominee.id);
      toast.success('The nomination was restored.');
      refresh();
    } catch (caught) {
      toast.failure(caught instanceof ApiError ? caught.message : 'Could not restore the nomination.');
    }
  }

  // The edit form states the employee and needs the detail; a failed fetch has no form to report in.
  function openEdit(nominee: AwardNomineeListItem) {
    nominationsApi
      .getNominee(nominee.id)
      .then((detail) => {
        setEditing(nominee);
        setEditingDetail(detail);
      })
      .catch((caught: unknown) => toast.failure(caught instanceof ApiError ? caught.message : 'Could not load this nomination.'));
  }

  const columns: DataColumn<AwardNomineeListItem>[] = [
    {
      key: 'name',
      header: 'Nominee',
      width: 260,
      // Not sortable: the name is in the other database.
      render: (n) => <TextCell value={nomineeName(n.personId, names)} />,
    },
    // Personnel's value — stripped by the server without the key, so the column goes with it.
    ...(canReadPersonnel
      ? [
          {
            key: 'employeeNumber',
            header: 'Employee no.',
            width: 160,
            summary: true,
            render: (n: AwardNomineeListItem) => <TextCell value={n.employeeNumber} mono />,
          },
        ]
      : []),
    {
      // ONE figure — the share of the checklist submitted, the attrition register's `Approved` column.
      key: 'submitted',
      header: 'Submitted',
      width: 110,
      align: 'right',
      summary: true,
      render: (n) => (
        <TextCell value={n.documentCount > 0 ? `${progressPercent(n.submittedCount, n.documentCount)}%` : '—'} mono numeric />
      ),
    },
    /*
      THE EVALUATION'S RESULT — Evaluation's value, stripped by the server on Nomination without
      `award-evaluations.read`, so the columns go with it. One value each: the rank, the average of
      the evaluators' totals, how many scored. Unscored is an em dash, never a zero.
    */
    // Qualification is read BEFORE anyone is scored, so its table carries no score columns.
    ...(canReadEvaluation && stage !== 'qualification'
      ? [
          {
            key: 'rank',
            header: 'Rank',
            width: 80,
            align: 'right' as const,
            render: (n: AwardNomineeListItem) => <TextCell value={n.rank != null ? String(n.rank) : '—'} mono />,
          },
          {
            key: 'score',
            header: `Average score (of ${award.maxTotal})`,
            width: 170,
            align: 'right' as const,
            sortable: true,
            summary: true,
            render: (n: AwardNomineeListItem) => (
              <TextCell value={n.averageScore != null ? n.averageScore.toFixed(2) : '—'} mono numeric />
            ),
          },
          {
            key: 'evaluatorCount',
            header: 'Evaluators',
            width: 110,
            align: 'right' as const,
            render: (n: AwardNomineeListItem) => <TextCell value={String(n.evaluatorCount ?? 0)} mono numeric />,
          },
        ]
      : []),
    /*
      THE STAGE'S OWN RESULT — one LIFECYCLE column beside the status pill (`stage: true`), a badge at
      tone `inactive` on every state: the word carries the meaning. Qualification adds its REASON.
    */
    ...(stage === 'qualification'
      ? [
          {
            key: 'qualificationRemarks',
            header: 'Qualification remarks',
            width: 280,
            render: (n: AwardNomineeListItem) => <TextCell value={n.qualificationRemarks} />,
          },
          {
            key: 'qualification',
            header: 'Qualification',
            width: 140,
            stage: true,
            render: (n: AwardNomineeListItem) => (
              <StatusText tone="inactive">{n.isQualified == null ? 'Not tagged' : n.isQualified ? 'Qualified' : 'Disqualified'}</StatusText>
            ),
          },
        ]
      : []),
    ...(stage === 'deliberation'
      ? [
          {
            key: 'winner',
            header: 'Deliberation',
            width: 130,
            stage: true,
            render: (n: AwardNomineeListItem) =>
              award.deliberatedAt == null ? (
                <TextCell value="—" />
              ) : (
                <StatusText tone="inactive">{n.isWinner ? 'Winner' : 'Not named'}</StatusText>
              ),
          },
        ]
      : []),
    ...(stage === 'awarding'
      ? [
          {
            key: 'awarded',
            header: 'Final awarding',
            width: 140,
            stage: true,
            render: (n: AwardNomineeListItem) =>
              award.awardedAt == null ? (
                <TextCell value="—" />
              ) : (
                <StatusText tone="inactive">{n.isAwarded ? 'Awarded' : 'Not approved'}</StatusText>
              ),
          },
        ]
      : []),
    // The NOMINATION's remarks — read at qualification, and on Nomination; the later stages read results.
    ...(stage == null || stage === 'qualification'
      ? [
          {
            key: 'remarks',
            header: 'Remarks',
            width: 320,
            render: (n: AwardNomineeListItem) => <TextCell value={n.remarks} />,
          },
        ]
      : []),
    {
      key: 'status',
      header: 'Status',
      width: 110,
      status: true,
      render: (n) => (n.isActive ? <StatusText tone="active">Active</StatusText> : <StatusText tone="inactive">Deleted</StatusText>),
    },
  ];

  const closedReason = award.isActive ? null : 'This award is deleted. Restore it before changing its nominees.';

  /*
    WHY A STAGE ACT IS CLOSED — disabled for STATE, saying why; hidden (by its key) from a reader who
    could never do it. The server refuses each of these too; this is the affordance, not the guard.
  */
  const stageClosed = !award.isActive
    ? 'This award is deleted.'
    : award.awardedAt != null
      ? 'The final awarding is recorded, so every stage is fixed.'
      : award.deliberatedAt != null
        ? 'The deliberation is recorded, so qualification and scores are fixed.'
        : null;

  const rowStageReason = (n: AwardNomineeListItem) =>
    stageClosed ??
    (!n.isActive ? 'This nomination is deleted.' : n.personId == null ? 'This nominee is above your clearance.' : null);

  // The score sheet needs the detail — its criteria and the caller's own sheet; a failed fetch has no form to report in.
  function openScore(nominee: AwardNomineeListItem) {
    evaluationsApi
      .getNominee(nominee.id)
      .then((detail) => setScoring({ nominee: detail, name: nomineeName(nominee.personId, names) }))
      .catch((caught: unknown) => toast.failure(caught instanceof ApiError ? caught.message : 'Could not load this nominee.'));
  }

  // Tags each selected nominee Qualified, one request each so each lands in its own History Logs.
  async function qualifyAll(selected: AwardNomineeListItem[]) {
    let done = 0;

    try {
      for (const nominee of selected) {
        await evaluationsApi.qualify(nominee.id, { isQualified: true, remarks: null });
        done += 1;
      }
      toast.success(`${done} ${done === 1 ? 'nominee was' : 'nominees were'} tagged Qualified.`);
    } catch (caught) {
      toast.failure(caught instanceof ApiError ? caught.message : 'Could not tag every nominee.');
    } finally {
      if (done > 0) refresh();
    }
  }

  // The stage's ONE verb, promoted into the row's gutter — Qualify at qualification, Score at evaluation.
  const stageQuickActions =
    stage === 'qualification'
      ? (n: AwardNomineeListItem) => [
          {
            key: `qualify-${n.id}`,
            label: 'Qualify',
            icon: <ClipboardCheck className="h-4 w-4" aria-hidden="true" />,
            permission: 'award-evaluations.qualify',
            disabledReason: rowStageReason(n),
            onClick: () => setQualifying(n),
          },
        ]
      : stage === 'evaluation'
        ? (n: AwardNomineeListItem) => [
            {
              key: `score-${n.id}`,
              label: 'Score',
              icon: <Star className="h-4 w-4" aria-hidden="true" />,
              permission: 'award-evaluations.score',
              disabledReason: rowStageReason(n),
              onClick: () => openScore(n),
            },
          ]
        : undefined;

  return (
    <div className={fill ? 'flex h-full min-h-0 min-w-0 flex-1 flex-col p-4' : 'flex min-w-0 flex-col p-4'}>
      <DataTablePanel<AwardNomineeListItem>
        id={stage ? `${surface.key}-nominees-${stage}` : `${surface.key}-nominees`}
        state={state}
        columns={columns}
        rowKey={(n) => n.id}
        fill={fill}
        searchPlaceholder="Search remarks"
        onCreate={writes ? () => setAdding(true) : undefined}
        createLabel="Nominate"
        createPermission="award-nominations.create"
        createDisabledReason={closedReason}
        onRowOpen={(n) => navigate(`${surface.basePath}/${award.id}/nominees/${n.id}`)}
        openPermission={surface.readPermission}
        onDelete={writes ? (n) => setDeleting([n]) : undefined}
        deletePermission="award-nominations.delete"
        deleteDisabledReason={(n) => (n.isActive ? null : 'This nomination is already deleted.')}
        // Selection exists where a BULK act does: Nomination's delete, Qualification's tag. Elsewhere on Evaluation it would select for nothing.
        selectable={writes || stage === 'qualification'}
        rowQuickActions={stageQuickActions}
        bulkActions={
          stage === 'qualification'
            ? (selected) => {
                const taggable = selected.filter((n) => rowStageReason(n) == null);

                return [
                  {
                    key: 'qualify-nominees',
                    label: `Tag ${taggable.length} Qualified`,
                    permission: 'award-evaluations.qualify',
                    disabledReason: stageClosed ?? (taggable.length === 0 ? 'No selected nominee can be tagged.' : null),
                    onClick: () => void qualifyAll(taggable),
                  },
                ];
              }
            : writes
            ? (selected) => {
                const deletable = selected.filter((n) => n.isActive);

                return [
                  {
                    key: 'delete-nominees',
                    label: `Delete ${deletable.length} nomination${deletable.length === 1 ? '' : 's'}`,
                    permission: 'award-nominations.delete',
                    destructive: true,
                    disabledReason:
                      closedReason ?? (deletable.length === 0 ? 'Every selected nomination is already deleted.' : null),
                    onClick: () => setDeleting(deletable),
                  },
                ];
              }
            : undefined
        }
        rowActions={
          writes
            ? (n) => [
                {
                  // In the menu rather than `onEdit`, which carries no disabled reason.
                  key: `edit-${n.id}`,
                  label: 'Edit',
                  icon: <Pencil className="h-4 w-4" aria-hidden="true" />,
                  permission: 'award-nominations.update',
                  disabledReason:
                    closedReason ??
                    (n.isActive ? (n.personId == null ? 'This nominee is above your clearance.' : null) : 'This nomination is deleted. Restore it before editing.'),
                  onClick: () => openEdit(n),
                },
                {
                  key: `restore-${n.id}`,
                  label: 'Restore',
                  icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
                  permission: 'award-nominations.delete',
                  disabledReason: n.isActive ? 'This nomination is not deleted.' : closedReason,
                  onClick: () => void restore(n),
                },
              ]
            : undefined
        }
        otherInfo={(n) => n}
        settingsItems={[
          {
            key: 'show-deleted',
            label: 'Show deleted',
            icon: showDeleted ? <Check className="h-4 w-4" aria-hidden="true" /> : undefined,
            onClick: () => setShowDeleted((current) => !current),
          },
        ]}
        emptyMessage={
          writes
            ? award.isActive
              ? 'No employee nominated yet. Nominate one to start collecting their documents.'
              : 'This award was deleted with no nominee registered.'
            : 'No employee has been nominated for this award yet.'
        }
        emptySearchMessage="No nominee's remarks match that search."
      />

      {adding || (editing !== null && editingDetail !== null) ? (
        <NomineeModal
          award={award}
          editing={editingDetail}
          employeeName={editing ? nomineeName(editing.personId, names) : null}
          onClose={() => {
            setAdding(false);
            setEditing(null);
            setEditingDetail(null);
          }}
          onSaved={(saved, options) => {
            refresh();
            if (options?.keepOpen) return;
            setAdding(false);
            setEditing(null);
            setEditingDetail(null);
            // The reader's own choice on the form; unticked, they stay on the award.
            if (!editing && options?.openProfile) navigate(`${surface.basePath}/${award.id}/nominees/${saved.id}`);
          }}
        />
      ) : null}

      {qualifying ? (
        <QualifyNomineeModal
          nominee={qualifying}
          employeeName={nomineeName(qualifying.personId, names)}
          onClose={() => setQualifying(null)}
          onSaved={() => {
            setQualifying(null);
            refresh();
          }}
        />
      ) : null}

      {scoring ? (
        <ScoreNomineeModal
          nominee={scoring.nominee}
          employeeName={scoring.name}
          onClose={() => setScoring(null)}
          onSaved={() => {
            setScoring(null);
            refresh();
          }}
        />
      ) : null}

      {deleting ? (
        <ConfirmDialog
          open
          onClose={() => {
            setDeleting(null);
            setError(null);
          }}
          onConfirm={() => void confirmDelete()}
          title={deleting.length === 1 ? 'Delete nomination' : `Delete ${deleting.length} nominations`}
          danger
          requireConfirm
          requireCredential
          loading={running}
          id="award-nominee-delete-dialog"
          body={
            <div className="flex flex-col gap-2">
              <p>
                {deleting.length === 1
                  ? `${nomineeName(deleting[0].personId, names) ?? 'This employee'} is taken off the nominees for ${award.categoryName}.`
                  : `${deleting.length} employees are taken off the nominees for ${award.categoryName}.`}
              </p>
              <p>Nothing is erased — their documents stay, and a nomination can be restored with Show deleted.</p>
              {error ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </div>
  );
}
